import { FiTrash2, FiEdit2 } from 'react-icons/fi';
import { formatDisplayShort } from '../../../../lib/dateUtils';
import TeamRoleBadge from './TeamRoleBadge';
import Button from '../../../../components/ui/Button';

/**
 * Team members list — desktop table and mobile cards block.
 * All state, mutations, and handlers stay in the parent (Team.jsx);
 * this component only renders and forwards events via props.
 */
export default function TeamMembersList({
  users,
  isAdminUser,
  currentUserId,
  handleEditUser,
  handleDeleteUser,
  isDeleting,
  setResetPasswordUser,
  setResetPasswordValue,
}) {
  return (
    <div className="card team-card">
      {/* Desktop: table */}
      <div className="team-table-desktop">
        <table className="table team-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Joined</th>
              {isAdminUser && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
                <td>{u.email || '—'}</td>
                <td><TeamRoleBadge role={u.role} /></td>
                <td>
                  {u.mustChangePassword ? (
                    <span className="team-status-pending" title="Must set new password on next login">Must change password</span>
                  ) : u.lastLoginAt ? (
                    <span className="team-status-ok">Last login: {formatDisplayShort(u.lastLoginAt)}</span>
                  ) : (
                    <span className="team-status-ok">Active</span>
                  )}
                </td>
                <td>{u.createdAt ? formatDisplayShort(u.createdAt) : '—'}</td>
                {isAdminUser && (
                  <td>
                    <div className="team-table-actions">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditUser(u)}
                        title="Edit team member"
                        aria-label={`Edit ${u.firstName || u.email}`}
                      >
                        <FiEdit2 />
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => { setResetPasswordUser(u); setResetPasswordValue(''); }}
                        title="Reset password (user must change on next login)"
                        aria-label={`Reset password for ${u.firstName || u.email}`}
                      >
                        Reset password
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="team-btn-delete"
                        onClick={() => handleDeleteUser(u)}
                        disabled={isDeleting || u.id === currentUserId}
                        title={u.id === currentUserId ? 'You cannot remove your own account' : 'Remove team member'}
                        aria-label={u.id === currentUserId ? 'Cannot remove self' : `Remove ${u.firstName || u.email}`}
                      >
                        <FiTrash2 />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile: cards (Name, Email, Role stacked) */}
      <div className="team-mobile-cards">
        {users.map((u) => (
          <div key={u.id} className="team-mobile-card">
            <div className="team-mobile-card-name">
              {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
            </div>
            <div className="team-mobile-card-email">{u.email || '—'}</div>
            <div className="team-mobile-card-role">
              <TeamRoleBadge role={u.role} />
            </div>
            <div className="team-mobile-card-status text-muted" style={{ fontSize: '0.875rem' }}>
              {u.mustChangePassword ? 'Must change password' : u.lastLoginAt ? `Last login: ${formatDisplayShort(u.lastLoginAt)}` : 'Active'}
            </div>
            {isAdminUser && (
              <div className="team-mobile-card-actions">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleEditUser(u)}
                  title="Edit"
                >
                  <FiEdit2 /> Edit
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => { setResetPasswordUser(u); setResetPasswordValue(''); }}
                  title="Reset password"
                >
                  Reset password
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="team-btn-delete team-mobile-btn-delete"
                  onClick={() => handleDeleteUser(u)}
                  disabled={isDeleting || u.id === currentUserId}
                  title={u.id === currentUserId ? 'You cannot remove your own account' : 'Remove team member'}
                  aria-label={u.id === currentUserId ? 'Cannot remove self' : `Remove ${u.firstName || u.email}`}
                >
                  <FiTrash2 /> Remove
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      {users.length === 0 && (
        <p className="empty-state">No team members yet. Invite someone to get started.</p>
      )}
    </div>
  );
}
